import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import '../styles/pages/PublicPages.css';

const API_URL = process.env.REACT_APP_API_URL;

function ContactPage() {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    managerName: '',
    contact: '',
    companyName: '',
    content: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [isSuccess, setIsSuccess] = useState(false);

  const nameRef = useRef(null);
  const contactRef = useRef(null);
  const companyRef = useRef(null);
  const contentRef = useRef(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (fieldErrors[name]) {
      setFieldErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const setFieldError = (key, msg, ref) => {
    setFieldErrors(prev => ({ ...prev, [key]: msg }));
    if (ref) setTimeout(() => ref.current?.focus(), 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setFieldErrors({});

    if (!formData.managerName.trim()) {
      setFieldError('managerName', '이름을 입력해주세요.', nameRef);
      return;
    }
    if (!formData.contact.trim()) {
      setFieldError('contact', '연락처를 입력해주세요.', contactRef);
      return;
    }
    if (!formData.companyName.trim()) {
      setFieldError('companyName', '회사명을 입력해주세요.', companyRef);
      return;
    }
    if (!formData.content.trim()) {
      setFieldError('content', '문의 내용을 입력해주세요.', contentRef);
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          managerName: formData.managerName,
          contact: formData.contact,
          companyName: formData.companyName,
          content: formData.content,
          createdAt: new Date().toISOString()
        })
      });

      const data = await response.json();

      if (response.ok) {
        setFormData({ managerName: '', contact: '', companyName: '', content: '' });
        setIsSuccess(true);
      } else {
        setError(data.message || '문의 요청에 실패했습니다.');
      }
    } catch (err) {
      console.error('문의 요청 에러:', err);
      setError('서버와 통신할 수 없습니다. 잠시 후 다시 시도하세요.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="contact-page">
      <Header />

      <div className="contact-page__body">
        <div className="contact-page__card">
          {/* 헤더 */}
          <div className="contact-page__header">
            <div className="contact-page__icon">📋</div>
            <h1 className="contact-page__title">광고 대행 문의하기</h1>
            <p className="contact-page__subtitle">
              전문 마케터팀이 빠르게 연락드리겠습니다.
            </p>
          </div>

          {/* 서버 에러 메시지 */}
          {error && (
            <p className="contact-page__error">{error}</p>
          )}

          {/* 폼 */}
          <form onSubmit={handleSubmit} className="contact-page__form">
            <div className="contact-page__field">
              <label className="contact-page__label">이름 <span className="contact-page__required">*</span></label>
              <input
                ref={nameRef}
                type="text"
                name="managerName"
                placeholder="담당자 이름을 입력하세요"
                value={formData.managerName}
                onChange={handleChange}
                disabled={loading}
                className={`contact-page__input${fieldErrors.managerName ? ' contact-page__input--error' : ''}`}
              />
              {fieldErrors.managerName && (
                <span className="contact-page__field-error">{fieldErrors.managerName}</span>
              )}
            </div>

            <div className="contact-page__field">
              <label className="contact-page__label">연락처 <span className="contact-page__required">*</span></label>
              <input
                ref={contactRef}
                type="text"
                name="contact"
                placeholder="연락 가능한 번호를 입력하세요"
                value={formData.contact}
                onChange={handleChange}
                disabled={loading}
                className={`contact-page__input${fieldErrors.contact ? ' contact-page__input--error' : ''}`}
              />
              {fieldErrors.contact && (
                <span className="contact-page__field-error">{fieldErrors.contact}</span>
              )}
            </div>

            <div className="contact-page__field">
              <label className="contact-page__label">회사명 <span className="contact-page__required">*</span></label>
              <input
                ref={companyRef}
                type="text"
                name="companyName"
                placeholder="회사명을 입력하세요"
                value={formData.companyName}
                onChange={handleChange}
                disabled={loading}
                className={`contact-page__input${fieldErrors.companyName ? ' contact-page__input--error' : ''}`}
              />
              {fieldErrors.companyName && (
                <span className="contact-page__field-error">{fieldErrors.companyName}</span>
              )}
            </div>

            <div className="contact-page__field">
              <label className="contact-page__label">문의 내용 <span className="contact-page__required">*</span></label>
              <textarea
                ref={contentRef}
                name="content"
                placeholder="광고 대행 관련 문의 내용을 자유롭게 작성해주세요"
                value={formData.content}
                onChange={handleChange}
                disabled={loading}
                className={`contact-page__textarea${fieldErrors.content ? ' contact-page__input--error' : ''}`}
              />
              {fieldErrors.content && (
                <span className="contact-page__field-error">{fieldErrors.content}</span>
              )}
            </div>

            <button type="submit" disabled={loading} className="contact-page__submit">
              {loading ? '전송 중...' : '문의 보내기'}
            </button>
          </form>
        </div>
      </div>

      {/* 성공 모달 */}
      {isSuccess && (
        <div className="contact-page__success-modal">
          <div className="contact-page__success-modal__content">
            <div className="contact-page__success-modal__icon">✓</div>
            <h2 className="contact-page__success-modal__title">문의 접수 완료</h2>
            <p className="contact-page__success-modal__message">
              문의가 정상적으로 접수되었습니다.<br />
              담당자 확인 후 연락드리겠습니다.
            </p>
            <button
              className="contact-page__success-modal__btn"
              onClick={() => { setIsSuccess(false); navigate('/'); }}
            >
              확인
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default ContactPage;
